/**
 * SMS host adapter for the shared Sources UI (`@tiaki/sources-ui`).
 *
 * Wires SMS-specific dependencies (AuthContext for permissions, the
 * typed `@tiaki/http-client` for HTTP, SMS's Chakra toaster) through the
 * lib's host-adapter contract. Where AIM has a single Redux-backed active
 * site, SMS provides the full list via `useSitesList`, which puts
 * <SourcesPage /> into multi-site mode (one collapsible section per site).
 *
 * AIM web and SMS web both render the same `<SourcesPage />` via this
 * adapter pattern.
 *
 * Uses the typed `@tiaki/http-client` per ADR 113 §Decision (the typed-
 * call-site DX win).
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import type {
  AlertTypeListParams,
  AlertTypeListResponse,
  IVendor,
  IVendorCreate,
  SourcesHost,
  SourcesSiteSummary,
  VendorListResponse,
  VendorSite,
} from '@tiaki/sources-ui';
import { client } from '../../lib/http-client';
import { toaster } from '../../components/ui/toaster';
import type { Site } from '../../services/sites.service';

interface UseSmsSourcesHostArgs {
  /** All sites the user can manage. The first entry seeds single-site
   *  fallbacks (clientId chip, default Add target). */
  sites: Site[];
}

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  const __resp = result as { error?: unknown }; if (__resp.error !== undefined) throw __resp.error;
  return result.data as T;
}

export function useSmsSourcesHost({
  sites,
}: UseSmsSourcesHostArgs): SourcesHost {
  return useMemo<SourcesHost>(() => {
    // Sites all belong to the same org, so client-id/token-secret are
    // org-scoped — pull them from the first site for the chips at the
    // page header. The list itself drives the per-site sections below.
    const firstSite = sites[0] ?? null;
    const sitesSummary: SourcesSiteSummary[] = sites.map((s) => ({
      id: s.id,
      name: s.name,
    }));
    return {
      useSiteContext: () => ({
        siteId: firstSite?.id ?? null,
        clientId: firstSite?.organization_public_id || '',
        // SMS does not currently surface organization_jwt_secret on the Site
        // payload — leaving empty cleanly hides the copy chip's value.
        tokenSecret: '',
        siteRecord: firstSite as Record<string, any> | null,
      }),
      useSitesList: () => sitesSummary,
      // SMS web doesn't ship the AIM-style Django permission system
      // (AuthContext.permissions doesn't reliably contain `vendors.*` codes
      // even for users with backend access — proven on dev where the same
      // user/site shows 3 vendors in AIM but 0 + no Add button in SMS).
      // The backend (`/api/v2/vendor/`) is still the source of truth and
      // returns 403 for unauthorized users, which surfaces as a toast.
      // Mirrors the rest of SMS, which doesn't gate UI on Django perms.
      usePermissions: () => ({
        canViewVendor: true,
        canAddVendor: true,
        canChangeVendor: true,
        canDeleteVendor: true,
      }),
      // SMS doesn't currently route VendorDetail socket events; returning
      // undefined makes the lib's live-merge effect a no-op.
      useVendorSocketUpdate: () => undefined,
      client: {
        listVendors: async (params: IVendor): Promise<VendorListResponse> => {
          const result = await client.GET('/api/v2/vendor/', {
            params: { query: params as never },
          });
          return unwrap(result) as unknown as VendorListResponse;
        },
        createVendor: async (payload: IVendorCreate): Promise<VendorSite> => {
          const result = await client.POST('/api/v2/vendor/', {
            body: payload as never,
          });
          return unwrap(result) as unknown as VendorSite;
        },
        updateVendor: async (
          id: string,
          payload: IVendorCreate,
        ): Promise<VendorSite> => {
          const result = await client.PATCH('/api/v2/vendor/{id}/', {
            params: { path: { id } as never },
            body: payload as never,
          });
          return unwrap(result) as unknown as VendorSite;
        },
        deleteVendor: async (id: string): Promise<void> => {
          const result = await client.DELETE('/api/v2/vendor/{id}/', {
            params: { path: { id } as never },
          });
          const __resp = result as { error?: unknown }; if (__resp.error !== undefined) throw __resp.error;
        },
        listAlertTypes: async (
          params: AlertTypeListParams,
        ): Promise<AlertTypeListResponse> => {
          const result = await client.GET('/api/v2/alert-type/', {
            params: { query: params as never },
          });
          return unwrap(result) as unknown as AlertTypeListResponse;
        },
        testSourceConnection: async (
          vendorId: number | string | null,
          payload?: Record<string, any>,
        ): Promise<{ success: boolean; message?: string }> => {
          const numericId = Number(vendorId);
          if (!vendorId || !numericId) {
            const result = await client.POST('/api/v2/vendor/test-connection-unsaved/', {
              body: payload ?? {},
            });
            return unwrap(result);
          }
          const result = await client.POST('/api/v2/vendor/{id}/test-connection/', {
            params: { path: { id: numericId } },
            body: payload ?? {},
          });
          return unwrap(result);
        },
      },
      toaster,
    };
  }, [sites]);
}
